import React from "react";
import { Box, Button, TextField, Typography, useMediaQuery } from "@mui/material";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import { useNavigate } from "react-router-dom";
import headerLogo from "../assets/images/headerlogo.svg";
import { identifyUser, captureEvent } from "../analytics/posthogClient";
import { sessionIsOnboarded } from "../utils/onboardingStatus";

const SOLVER_API_URL = process.env.REACT_APP_SOLVER_API_URL || "http://localhost:3001";

const COLORS = {
  bg: "#121212",
  panel: "#1a1a1a",
  border: "#2e2e2e",
  text: "#e8e8e8",
  muted: "#9a9a9a",
  green: "#56ECCF",
  gold: "#B99E4F",
  white: "#ffffff",
};

const BENEFITS = [
  {
    title: "Professional insights",
    body: "See exact GTO frequencies and EV for every decision, the same standard cash pros study.",
    Icon: InsightsOutlinedIcon,
  },
  {
    title: "Perfect plays, immediately",
    body: "Train and solve spots so you know the right line before the next session starts.",
    Icon: BoltOutlinedIcon,
  },
  {
    title: "Track toward cash pro",
    body: "Measure accuracy, climb stakes with confidence, and build a real improvement loop.",
    Icon: TrendingUpOutlinedIcon,
  },
];

const AUTH_BTN_SX = {
  backgroundColor: COLORS.green,
  borderColor: COLORS.green,
  color: "#111",
  textTransform: "none",
  fontWeight: 700,
  "&:hover": { backgroundColor: "rgb(45, 179, 155)", borderColor: "rgb(45, 179, 155)" },
  "&.Mui-disabled": { opacity: 0.55, color: "#444" },
};

const INPUT_SX = {
  "& .MuiOutlinedInput-input": { color: COLORS.white, fontSize: "0.85rem" },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#141414",
    "& fieldset": { borderColor: "rgba(255,255,255,0.25)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.45)" },
    "&.Mui-focused fieldset": { borderColor: COLORS.green },
  },
};

export default function WelcomeAuth() {
  const navigate = useNavigate();
  const isNarrow = useMediaQuery("(max-width:900px)");
  const [mode, setMode] = React.useState("signup"); // login | signup | reset
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState("");
  const [info, setInfo] = React.useState("");
  const [devCode, setDevCode] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [code, setCode] = React.useState("");
  const [signupCodeSent, setSignupCodeSent] = React.useState(false);
  const [resetCodeSent, setResetCodeSent] = React.useState(false);
  const [newPassword, setNewPassword] = React.useState("");
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${SOLVER_API_URL}/api/auth/session`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (cancelled || !res.ok || !(data?.success || data?.verified)) return;
        if (!sessionIsOnboarded(data)) {
          navigate("/onboarding", { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      } catch (_) {}
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const call = async (path, body = null) => {
    const res = await fetch(`${SOLVER_API_URL}${path}`, {
      method: body ? "POST" : "GET",
      credentials: "include",
      headers: body ? { "Content-Type": "application/json" } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    return { ok: res.ok, data };
  };

  const clearMessages = () => {
    setError("");
    setInfo("");
    setDevCode("");
  };

  const switchToMode = (next) => {
    clearMessages();
    setMode(next);
    setSignupCodeSent(false);
    setResetCodeSent(false);
    setCode("");
    setNewPassword("");
  };

  const afterAuth = async (loggedInEmail, loginPayload = null) => {
    // Prefer login payload (includes onboardingCompleted) so we do not mis-route
    // before /session is consistent.
    if (sessionIsOnboarded(loginPayload)) {
      const emailAddr = String(loginPayload?.email || loggedInEmail || "").trim().toLowerCase();
      identifyUser({ email: emailAddr, name: loginPayload?.name });
      window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
      navigate("/", { replace: true });
      return;
    }
    const session = await call("/api/auth/session");
    if (!session?.ok || !(session?.data?.success || session?.data?.verified)) {
      setError("Logged in, but session could not be verified. Please try again.");
      return;
    }
    const emailAddr = String(session?.data?.email || loggedInEmail || "").trim().toLowerCase();
    identifyUser({ email: emailAddr, name: session?.data?.name });
    window.dispatchEvent(new CustomEvent("pm:billing-refresh"));
    if (sessionIsOnboarded(session?.data)) {
      navigate("/", { replace: true });
    } else {
      navigate("/onboarding", { replace: true });
    }
  };

  const doLogin = async () => {
    clearMessages();
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/login", { email, password });
      if (!ok || !data?.success) return setError(data?.error || "Login failed.");
      captureEvent("user_logged_in");
      await afterAuth(data?.email || email, data);
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doSignupStart = async () => {
    clearMessages();
    const e = String(email || "").trim();
    const p = String(password || "").trim();
    if (!emailRe.test(e) || p.length < 8) {
      setError("Enter a valid email and a password of at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/signup/start", { email: e, password: p });
      if (!ok || !data?.success) return setError(data?.error || "Signup failed.");
      setSignupCodeSent(true);
      setInfo("Verification code sent. Enter the code to finish signup.");
      if (data?.devCode) setDevCode(String(data.devCode));
      if (data?.mailError) setError(`SMTP: ${data.mailError}`);
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doSignupVerify = async () => {
    clearMessages();
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/signup/verify", { email, code });
      if (!ok || !data?.success) return setError(data?.error || "Verification failed.");
      captureEvent("user_signed_up", { email: String(email || "").trim().toLowerCase() });
      const login = await call("/api/auth/login", { email, password });
      if (!login.ok || !login.data?.success) {
        setInfo("Signup verified. Please login.");
        setMode("login");
        setSignupCodeSent(false);
        setCode("");
        return;
      }
      await afterAuth(email);
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doResetStart = async () => {
    clearMessages();
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/password/reset/start", { email });
      if (!ok || !data?.success) return setError(data?.error || "Could not start reset.");
      setResetCodeSent(true);
      setInfo("Reset code sent. Enter code and new password.");
      if (data?.devCode) setDevCode(String(data.devCode));
      if (data?.mailError) setError(`SMTP: ${data.mailError}`);
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const doResetConfirm = async () => {
    clearMessages();
    setBusy(true);
    try {
      const { ok, data } = await call("/api/auth/password/reset/confirm", {
        email,
        code,
        newPassword,
      });
      if (!ok || !data?.success) return setError(data?.error || "Reset failed.");
      setInfo("Password reset complete. Please login.");
      setMode("login");
      setResetCodeSent(false);
      setCode("");
      setNewPassword("");
    } catch (_) {
      setError("Could not contact server.");
    } finally {
      setBusy(false);
    }
  };

  const submitPrimary = () => {
    if (mode === "login") return doLogin();
    if (mode === "signup") {
      if (!signupCodeSent) return doSignupStart();
      return doSignupVerify();
    }
    if (!resetCodeSent) return doResetStart();
    return doResetConfirm();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        gridTemplateColumns: isNarrow ? "1fr" : "1.05fr 0.95fr",
        background: `radial-gradient(1200px 600px at 10% -10%, rgba(86,236,207,0.12), transparent 55%),
          radial-gradient(900px 500px at 90% 110%, rgba(185,158,79,0.1), transparent 50%),
          ${COLORS.bg}`,
        color: COLORS.text,
      }}
    >
      <Box
        sx={{
          display: isNarrow ? "none" : "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { md: 6, lg: 8 },
          py: 6,
          borderRight: `1px solid ${COLORS.border}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 4 }}>
          <img src={headerLogo} alt="PokerMoose" style={{ height: 40, width: 40, opacity: 0.85 }} />
          <Typography sx={{ fontFamily: "Play, Arial, sans-serif", fontWeight: 800, fontSize: 22, letterSpacing: "0.06em", textTransform: "uppercase" }}>
            PokerMoose
          </Typography>
        </Box>
        <Typography sx={{ fontSize: 32, fontWeight: 800, color: COLORS.white, lineHeight: 1.2, mb: 1.5, maxWidth: 460, fontFamily: "Play, Arial, sans-serif" }}>
          Train like a cash pro. Play with an edge.
        </Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: 15, lineHeight: 1.5, mb: 3.5, maxWidth: 440 }}>
          Seven days free: Trainer, Play, and Solver. No card required.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.2 }}>
          {BENEFITS.map((b) => {
            const Icon = b.Icon;
            return (
              <Box key={b.title} sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                <Icon sx={{ color: COLORS.green, fontSize: 26, mt: 0.15, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ color: COLORS.white, fontWeight: 700, fontSize: 15, mb: 0.3 }}>{b.title}</Typography>
                  <Typography sx={{ color: COLORS.muted, fontSize: 13.5, lineHeight: 1.45 }}>{b.body}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", px: { xs: 2, sm: 4 }, py: 4 }}>
        <Box
          sx={{
            width: "100%",
            maxWidth: 420,
            backgroundColor: COLORS.panel,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 2,
            p: { xs: 2.2, sm: 3 },
            display: "flex",
            flexDirection: "column",
            gap: 1.2,
          }}
        >
          {isNarrow && (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <img src={headerLogo} alt="PokerMoose" style={{ height: 36, opacity: 0.7 }} />
            </Box>
          )}
          <Typography sx={{ color: COLORS.white, fontWeight: 800, fontSize: 18, textAlign: "center", mb: 0.5 }}>
            {mode === "login" ? "Welcome back" : mode === "signup" ? "Start your free trial" : "Reset password"}
          </Typography>
          {mode === "signup" && (
            <Typography sx={{ color: COLORS.muted, fontSize: 12.5, textAlign: "center", mb: 0.5 }}>
              No credit card required
            </Typography>
          )}

          <TextField size="small" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} sx={INPUT_SX} disabled={busy} />
          {mode !== "reset" && (
            <TextField
              size="small"
              type="password"
              placeholder={mode === "signup" ? "Password (min 8 chars)" : "Password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={INPUT_SX}
              disabled={busy}
            />
          )}
          {mode === "signup" && signupCodeSent && (
            <TextField size="small" placeholder="Verification code" value={code} onChange={(e) => setCode(e.target.value)} sx={INPUT_SX} disabled={busy} />
          )}
          {mode === "reset" && (
            <>
              <TextField size="small" placeholder="Reset code" value={code} onChange={(e) => setCode(e.target.value)} sx={INPUT_SX} disabled={busy} />
              <TextField
                size="small"
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                sx={INPUT_SX}
                disabled={busy}
              />
            </>
          )}

          <Button variant="contained" onClick={submitPrimary} disabled={busy} sx={{ ...AUTH_BTN_SX, mt: 0.5, py: 1 }}>
            {busy
              ? "Please wait..."
              : mode === "login"
                ? "Login"
                : mode === "signup"
                  ? signupCodeSent
                    ? "Verify & continue"
                    : "Create account"
                  : resetCodeSent
                    ? "Set new password"
                    : "Send reset code"}
          </Button>

          <Typography sx={{ color: COLORS.muted, fontSize: 12, textAlign: "center", mt: 0.5 }}>
            {mode !== "signup" && (
              <>
                New here?{" "}
                <Box component="span" onClick={() => switchToMode("signup")} sx={{ color: COLORS.gold, cursor: "pointer" }}>
                  Sign up
                </Box>
                {" · "}
              </>
            )}
            {mode !== "login" && (
              <>
                Have an account?{" "}
                <Box component="span" onClick={() => switchToMode("login")} sx={{ color: "#9ecbff", cursor: "pointer" }}>
                  Login
                </Box>
                {" · "}
              </>
            )}
            <Box component="span" onClick={() => switchToMode("reset")} sx={{ color: "#6aa6ff", cursor: "pointer" }}>
              Reset password
            </Box>
          </Typography>

          {error && <Typography sx={{ color: "#ff7d7d", fontSize: 12 }}>{error}</Typography>}
          {info && <Typography sx={{ color: "#9ecbff", fontSize: 12 }}>{info}</Typography>}
          {!!devCode && (
            <Typography sx={{ color: "#d8d8d8", fontSize: 11 }}>
              Dev code: <b>{devCode}</b>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
